import React from 'react';
import { Box, Typography, Breadcrumbs, Link, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const PageHeader = ({ 
  title, 
  subtitle, 
  breadcrumbs = [], 
  action,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();

  return (
    <Box sx={{ mb: 3.5 }}>
      {/* Breadcrumbs - always show container for consistent spacing */}
      {breadcrumbs.length > 0 ? (
        <Breadcrumbs
          separator={
            <Typography 
              sx={{ 
                color: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)',
                fontFamily: "'Space Mono', monospace",
                fontSize: '0.72rem',
                mx: 0.5,
              }}
            >
              /
            </Typography>
          }
          sx={{ mb: 1.5 }}
        >
          <Link
            component="button"
            onClick={() => navigate('/')}
            sx={{
              fontFamily: "'Space Mono', monospace",
              fontSize: '0.72rem',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
              textDecoration: 'none',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              padding: 0,
              transition: 'color 0.12s ease',
              '&:hover': {
                color: theme.palette.primary.main,
              },
            }}
          >
            Home
          </Link>
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            return isLast ? (
              <Typography
                key={index}
                sx={{ 
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                }}
              >
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={index}
                component="button"
                onClick={() => crumb.path && navigate(crumb.path)}
                sx={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
                  textDecoration: 'none',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'color 0.12s ease',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      ) : (
        <Box sx={{ height: 20, mb: 1.5 }} />
      )}

      {/* Title and Action */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box>
          {/* Main Title */}
          <Typography 
            variant="h1" 
            sx={{ 
              fontFamily: "'Space Mono', monospace",
              fontSize: '1.4rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              lineHeight: 1.2,
              mb: subtitle ? 0.75 : 0,
              color: theme.palette.text.primary,
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)',
                fontFamily: "'Space Mono', monospace",
                fontSize: '0.78rem',
                lineHeight: 1.5,
                maxWidth: 500,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Action button area */}
        {action && (
          <Box sx={{ flexShrink: 0, pt: 0.5 }}>
            {action}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PageHeader;
